import React, { useState, useEffect } from 'react';
import logoBlack from './assets/hazdex-blue-black.png';

const API_ENDPOINT = 'https://vk4uyaz4u9.execute-api.us-east-1.amazonaws.com/prod';

function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization_name: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);

  // Preload image
  useEffect(() => {
    const img = new Image();
    img.src = logoBlack;
    img.onload = () => setImageLoaded(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_ENDPOINT}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!imageLoaded) {
    return (
      <div className="min-h-screen bg-[#353535] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#353535] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="flex justify-center mb-6">
            <img 
              src={logoBlack}
              alt="HAZDEX" 
              style={{ height: '180px', width: 'auto' }}
            />
          </div>

          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Submitted!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for your interest in HAZDEX. Your registration request has been submitted successfully.
            </p>
            <p className="text-gray-600 mb-6">
              An administrator will review your request and you will receive an email once your account is approved.
            </p>
            
            
            <a href="/login"
              className="inline-block w-full bg-[#00b7ff] text-white py-3 rounded-lg hover:bg-[#00aaee] transition font-medium shadow-lg"
            >
              Return to Login
            </a>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Powered by <span className="font-semibold text-gray-700">ADAPT Readiness and Resilience</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#353535] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img 
            src={logoBlack}
            alt="HAZDEX" 
            style={{ height: '180px', width: 'auto' }}
          />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">Request Access</h1>
        <p className="text-[#00b7ff] font-semibold mb-2 text-center">Join HAZDEX</p>
        <p className="text-gray-600 mb-8 text-center text-sm">Fill out the form below to request access to the platform</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Registration Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00b7ff] focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00b7ff] focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label htmlFor="organization_name" className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="organization_name"
              name="organization_name"
              required
              value={formData.organization_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00b7ff] focus:border-transparent"
              placeholder="Acme Emergency Services"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-3 rounded-lg text-white font-medium shadow-lg transition ${
              submitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#00b7ff] hover:bg-[#00aaee]'
            }`}
          >
            {submitting ? 'Submitting...' : 'Request Access'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <a href="/login" className="text-[#00b7ff] hover:text-[#00aaee] font-medium">
              Sign In
            </a>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            Powered by <span className="font-semibold text-gray-700">ADAPT Readiness and Resilience</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;